import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_928e083d-76f5-471c-8405-fe8d0803371a/artifacts/i09y221l_BSM%20Welfare%20LOGO%202%20%281%29.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Focus Areas", path: "/focus-areas" },
  { name: "Programs", path: "/programs" },
  { name: "Get Involved", path: "/get-involved" },
  { name: "Governance", path: "/governance" },
  { name: "Updates", path: "/updates" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-[#E2E5E3]/50 py-2 shadow-sm"
          : "bg-transparent py-4"
      }`}
      style={!isScrolled ? { textShadow: "0 1px 4px rgba(0,0,0,0.3)" } : {}}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <img src={LOGO_URL} alt="BSM Welfare Foundation" className="h-12 w-auto" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
              className={`nav-link-underline px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                location.pathname === link.path
                  ? "text-[#E28743] nav-link-active"
                  : `${isScrolled ? "text-[#2C3531]" : "text-white"} hover:text-[#E28743]`
              }`}
            >
              {link.name}
            </Link>
          ))}
          <Link
            to="/get-involved"
            data-testid="donate-header-button"
            className="ml-3 bg-[#E28743] text-white hover:bg-[#C96A27] rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-[0_4px_14px_rgba(226,135,67,0.3)] btn-press"
          >
            Donate
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 rounded-xl hover:bg-[#F1F3F2] transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden absolute top-full left-0 w-full bg-white/95 backdrop-blur-xl border-b border-[#E2E5E3] shadow-lg animate-fade-in"
        >
          <div className="px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.path.replace("/", "") || "home"}`}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "bg-[#E28743]/10 text-[#E28743]"
                    : "text-[#2C3531] hover:bg-[#F1F3F2]"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/get-involved"
              data-testid="mobile-donate-button"
              className="block mt-3 bg-[#E28743] text-white text-center rounded-full px-6 py-3 text-sm font-medium"
            >
              Donate
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
